/**
 * "Бизнес-аналитика" card
 *  - Competitor mentions
 *  - Product feedback
 *  - Market signals
 *  - Compliance flags
 */

import { useTranslation } from 'react-i18next';
import { AnalysisSection, Tag, type XMLAnalysisResult } from './analysis-utils';

interface BusinessIntelligenceSectionProps {
  result: XMLAnalysisResult;
}

const SENTIMENT_VARIANT: Record<string, 'success' | 'neutral' | 'danger'> = {
  positive: 'success',
  neutral: 'neutral',
  negative: 'danger',
};

const SEVERITY_VARIANT: Record<string, 'success' | 'warning' | 'danger'> = {
  praise: 'success',
  minor_issue: 'warning',
  major_issue: 'danger',
  incident: 'danger',
};

export function BusinessIntelligenceSection({
  result,
}: BusinessIntelligenceSectionProps) {
  const { t } = useTranslation('records');
  const bi = result.businessIntelligence;

  const competitorMentions = bi?.competitorMentions ?? [];
  const productFeedback = bi?.productFeedback ?? [];
  const marketSignals = bi?.marketSignals ?? [];
  const complianceFlags = bi?.complianceFlags ?? [];

  const total =
    competitorMentions.length +
    productFeedback.length +
    marketSignals.length +
    complianceFlags.length;

  if (total === 0) return null;

  return (
    <AnalysisSection
      title={t('records.analysisPanel.businessIntelligence', 'Business Intelligence')}
      summary={<span className="font-mono text-2xs text-fg-subtle">{total}</span>}
    >
      <div className="flex flex-col gap-3 text-xs">
        {competitorMentions.length > 0 && (
          <div>
            <div className="mb-1 font-mono text-micro font-semibold uppercase tracking-wide text-fg-muted">
              {t('records.analysisPanel.competitorMentions', 'Competitor mentions')}
            </div>
            <div className="flex flex-col gap-1">
              {competitorMentions.map((m, i) => (
                <div key={`${m.name}-${i}`} className="flex items-start gap-2">
                  <Tag
                    variant={SENTIMENT_VARIANT[m.sentiment] ?? 'neutral'}
                    className="text-micro py-0"
                  >
                    {m.sentiment}
                  </Tag>
                  <div className="flex-1">
                    <span className="font-medium text-fg">{m.name}</span>
                    <span className="italic text-fg-secondary">
                      {' '}
                      — &ldquo;{m.context}&rdquo;
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {productFeedback.length > 0 && (
          <div>
            <div className="mb-1 font-mono text-micro font-semibold uppercase tracking-wide text-fg-muted">
              {t('records.analysisPanel.productFeedback', 'Product feedback')}
            </div>
            <div className="flex flex-col gap-1">
              {productFeedback.map((fb, i) => (
                <div key={`${fb.product}-${i}`} className="flex items-start gap-2">
                  <Tag
                    variant={SEVERITY_VARIANT[fb.severity] ?? 'neutral'}
                    className="text-micro py-0"
                  >
                    {fb.severity}
                  </Tag>
                  <div className="flex-1">
                    <span className="font-medium text-fg">{fb.product}</span>
                    <span className="text-fg-secondary"> — {fb.feedback}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {marketSignals.length > 0 && (
          <div>
            <div className="mb-1 font-mono text-micro font-semibold uppercase tracking-wide text-fg-muted">
              {t('records.analysisPanel.marketSignals', 'Market signals')}
            </div>
            <div className="flex flex-col gap-1">
              {marketSignals.map((sig, i) => (
                <div key={`${sig.type}-${i}`} className="flex items-start gap-2">
                  <Tag variant="neutral" className="text-micro py-0 uppercase">
                    {sig.type}
                  </Tag>
                  <span className="flex-1 text-fg-secondary">{sig.signal}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {complianceFlags.length > 0 && (
          <div>
            <div className="mb-1 font-mono text-micro font-semibold uppercase tracking-wide text-fg-muted">
              {t('records.analysisPanel.complianceFlags', 'Compliance flags')}
            </div>
            <div className="flex flex-col gap-1">
              {complianceFlags.map((flag, i) => (
                <div
                  key={`${flag.flag}-${i}`}
                  className="flex items-start gap-2 rounded bg-danger-dim px-2.5 py-1.5"
                >
                  <Tag variant="danger" className="text-micro py-0">
                    {flag.flag}
                  </Tag>
                  <span className="flex-1 italic text-fg-secondary">
                    &ldquo;{flag.context}&rdquo;
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AnalysisSection>
  );
}
